import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { myRfqDrafts, rfqCategories, paymentTermOptions, shippingPorts } from "@/mocks/rfq";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const rfqJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "买家采购需求中心 | 皇冠鸿运商城",
  "description": "发布大批量采购需求，直连认证工厂获取最优报价。整车整柜采购，货款平台托管，报价信息仅对认证工厂可见。",
  "url": `${SITE_URL}/buyer-rfq`,
  "noIndex": true
};

type RfqStatus = "已发布" | "草稿" | "已关闭";

interface RfqItem {
  id: string;
  title: string;
  category: string;
  quantity: string;
  unit: string;
  targetPrice: string;
  deadline: string;
  status: RfqStatus;
  responses: number;
  createdAt: string;
  description: string;
  specs: string[];
  shippingPort: string;
  paymentTerms: string;
}

const statusColors: Record<RfqStatus, string> = {
  "已发布": "bg-green-100 text-green-600",
  "草稿": "bg-gray-100 text-gray-500",
  "已关闭": "bg-red-100 text-red-500",
};

export default function BuyerRfqPage() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState<RfqItem[]>(myRfqDrafts as RfqItem[]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "published" | "draft">("all");
  const [specInput, setSpecInput] = useState("");
  const [specs, setSpecs] = useState<string[]>([]);

  const [form, setForm] = useState({
    title: "",
    category: "",
    quantity: "",
    unit: "件",
    minPrice: "",
    maxPrice: "",
    deadline: "",
    description: "",
    shippingPort: "",
    paymentTerms: "",
    truckLoads: "",
    containerType: "20GP",
  });

  const resetForm = () => {
    setForm({ title: "", category: "", quantity: "", unit: "件", minPrice: "", maxPrice: "", deadline: "", description: "", shippingPort: "", paymentTerms: "", truckLoads: "", containerType: "20GP" });
    setSpecs([]);
    setSpecInput("");
    setEditingId(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (rfq: RfqItem) => {
    setForm({
      title: rfq.title,
      category: rfq.category,
      quantity: rfq.quantity.replace(/[^0-9]/g, ""),
      unit: rfq.unit,
      minPrice: "",
      maxPrice: "",
      deadline: rfq.deadline,
      description: rfq.description,
      shippingPort: rfq.shippingPort,
      paymentTerms: rfq.paymentTerms,
      truckLoads: "",
      containerType: "20GP",
    });
    setSpecs(rfq.specs);
    setEditingId(rfq.id);
    setShowForm(true);
  };

  const handleAddSpec = () => {
    if (specInput.trim()) {
      setSpecs((prev) => [...prev, specInput.trim()]);
      setSpecInput("");
    }
  };

  const handleRemoveSpec = (idx: number) => {
    setSpecs((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePublish = (asDraft: boolean) => {
    const newRfq: RfqItem = {
      id: editingId || `RFQ-2026-${String(rfqs.length + 1).padStart(3, "0")}`,
      title: form.title || "未命名采购需求",
      category: form.category || "其他",
      quantity: `${form.quantity}${form.unit}`,
      unit: form.unit,
      targetPrice: form.minPrice && form.maxPrice ? `¥${form.minPrice}–${form.maxPrice}/${form.unit}` : "面议",
      deadline: form.deadline || "2026-06-30",
      status: asDraft ? "草稿" : "已发布",
      responses: 0,
      createdAt: new Date().toISOString().slice(0, 10),
      description: form.description,
      specs,
      shippingPort: form.shippingPort || "广州港",
      paymentTerms: form.paymentTerms || "平台托管付款",
    };

    if (editingId) {
      setRfqs((prev) => prev.map((r) => r.id === editingId ? newRfq : r));
    } else {
      setRfqs((prev) => [newRfq, ...prev]);
    }

    setSubmitSuccess(true);
    setTimeout(() => {
      setSubmitSuccess(false);
      setShowForm(false);
      resetForm();
    }, 2000);
  };

  const handleDelete = (id: string) => {
    setRfqs((prev) => prev.filter((r) => r.id !== id));
  };

  const handleClose = (id: string) => {
    setRfqs((prev) => prev.map((r) => r.id === id ? { ...r, status: "已关闭" as RfqStatus } : r));
  };

  const filtered = rfqs.filter((r) => {
    if (activeTab === "published") return r.status === "已发布";
    if (activeTab === "draft") return r.status === "草稿";
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="买家采购需求中心 | 大批量采购报价 | 皇冠鸿运商城"
        description="发布大批量采购需求，直连认证工厂获取最优报价。整车整柜采购，货款平台托管，报价信息仅对认证工厂可见。"
        keywords="大批量采购,工厂报价,整柜采购,采购需求,皇冠鸿运商城"
        canonical="/buyer-rfq"
        jsonLd={rfqJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 买家采购需求中心</span>
          <a href="/" className="hover:underline cursor-pointer flex items-center gap-1">
            <i className="ri-home-line"></i> 返回首页
          </a>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png" alt="Logo" className="h-9 w-auto object-contain" />
              <span className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>皇冠鸿运商城</span>
            </a>
            <span className="text-gray-300 hidden sm:block">|</span>
            <span className="text-gray-600 text-sm font-medium hidden sm:block">买家采购需求中心</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5">
              <i className="ri-lock-line text-orange-500 text-sm"></i>
              <span className="text-orange-600 text-xs font-medium">报价信息仅对认证工厂可见</span>
            </div>
            <button
              onClick={handleOpenNew}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>发布采购需求
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero info banner */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-truck-line text-white text-2xl"></i>
                </div>
                <div>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif" }}>大批量采购需求发布</h1>
                  <p className="text-white/75 text-sm">整车 / 整柜采购，直连认证工厂，获取最优报价</p>
                </div>
              </div>
              <p className="text-white/80 text-sm leading-relaxed max-w-xl">
                发布您的采购需求（整车、整柜批量），平台将推送给所有通过认证的工厂会员。
                <strong className="text-yellow-300">报价信息完全保密</strong>，仅已付费认证的工厂会员可查看，
                公众无法看到您的采购详情，保护您的商业隐私。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 flex-shrink-0">
              {[
                { icon: "ri-lock-line", label: "信息保密", sub: "公众不可见" },
                { icon: "ri-building-4-line", label: "认证工厂", sub: "才能查看" },
                { icon: "ri-money-cny-circle-line", label: "货款托管", sub: "安全交易" },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mx-auto mb-1">
                    <i className={`${item.icon} text-white text-lg`}></i>
                  </div>
                  <p className="text-white font-semibold text-xs">{item.label}</p>
                  <p className="text-white/60 text-xs">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Privacy notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
            <i className="ri-eye-off-line text-amber-600 text-lg"></i>
          </div>
          <div>
            <p className="text-amber-700 font-semibold text-sm">采购信息隐私保护说明</p>
            <p className="text-amber-600 text-xs mt-1 leading-relaxed">
              您发布的所有采购需求（包括产品规格、目标价格、数量、联系方式）对普通公众完全隐藏。
              只有通过平台严格认证、并已缴纳会员费的工厂会员，才能在其专属后台查看您的报价需求。
              平台不会将您的信息泄露给任何未认证方。
            </p>
          </div>
        </div>

        {/* My RFQs */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-gray-800 font-bold text-lg">我的采购需求</h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              {([
                { id: "all", label: "全部" },
                { id: "published", label: "已发布" },
                { id: "draft", label: "草稿" },
              ] as { id: "all" | "published" | "draft"; label: string }[]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id ? "bg-white text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-file-list-3-line text-gray-300 text-3xl"></i>
              </div>
              <p className="text-gray-400 text-sm mb-4">暂无采购需求</p>
              <button
                onClick={handleOpenNew}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i>发布第一条采购需求
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map((rfq) => (
                <div key={rfq.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[rfq.status]}`}>
                          {rfq.status}
                        </span>
                        <span className="bg-gray-100 text-gray-500 text-xs px-2.5 py-1 rounded-full">{rfq.category}</span>
                        <span className="text-gray-300 text-xs">{rfq.id}</span>
                        <div className="flex items-center gap-1 bg-orange-50 text-orange-500 text-xs px-2 py-0.5 rounded-full">
                          <i className="ri-lock-line text-xs"></i>
                          <span>仅认证工厂可见</span>
                        </div>
                      </div>
                      <h3 className="text-gray-800 font-bold text-base mb-2">{rfq.title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2 mb-3">{rfq.description}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-stack-line text-gray-400"></i>
                          采购量：<strong className="text-gray-700">{rfq.quantity}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-price-tag-3-line text-gray-400"></i>
                          目标价：<strong className="text-red-500">{rfq.targetPrice}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line text-gray-400"></i>
                          截止：<strong className="text-gray-700">{rfq.deadline}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-ship-line text-gray-400"></i>
                          {rfq.shippingPort}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(rfq)}
                        className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>编辑
                      </button>
                      {rfq.status === "草稿" && (
                        <button
                          onClick={() => setRfqs((prev) => prev.map((r) => r.id === rfq.id ? { ...r, status: "已发布" as RfqStatus } : r))}
                          className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line"></i>发布
                        </button>
                      )}
                      {rfq.status === "已发布" && (
                        <button
                          onClick={() => handleClose(rfq.id)}
                          className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-close-circle-line"></i>关闭
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(rfq.id)}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-xl text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RFQ Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-gray-800 font-bold text-lg">
                  {editingId ? "编辑采购需求" : "发布新采购需求"}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <i className="ri-lock-line text-orange-500 text-xs"></i>
                  <p className="text-orange-500 text-xs font-medium">报价信息仅对已认证付费工厂会员可见，公众不可见</p>
                </div>
              </div>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {submitSuccess ? (
              <div className="p-10 text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
                  <i className="ri-check-line text-green-500 text-4xl"></i>
                </div>
                <p className="text-gray-800 font-bold text-xl mb-2">采购需求已发布！</p>
                <p className="text-gray-500 text-sm">已通知所有认证工厂会员，您将陆续收到报价</p>
                <div className="mt-4 bg-orange-50 rounded-xl p-4 text-left">
                  <div className="flex items-center gap-2 text-orange-600 text-sm">
                    <i className="ri-lock-line flex-shrink-0"></i>
                    <span>您的采购信息已加密保护，仅认证工厂可见，公众无法查看</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Product info */}
                <div>
                  <h4 className="text-gray-700 font-semibold text-sm mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500 rounded-full text-white text-xs font-bold">1</span>
                    产品基本信息
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">采购标题 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="例：大批量采购不锈钢保温杯（整柜）"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">产品品类 <span className="text-red-500">*</span></label>
                        <select
                          value={form.category}
                          onChange={(e) => setForm({ ...form, category: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-red-400"
                        >
                          <option value="">请选择品类</option>
                          {rfqCategories.map((c) => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs font-medium block mb-1.5">采购数量 <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                            placeholder="数量"
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                          />
                          <select
                            value={form.unit}
                            onChange={(e) => setForm({ ...form, unit: e.target.value })}
                            className="w-20 border border-gray-200 rounded-lg px-2 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-red-400"
                          >
                            {["件", "箱", "吨", "千克", "套", "双", "米"].map((u) => <option key={u}>{u}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">整车/整柜数量</label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={form.truckLoads}
                          onChange={(e) => setForm({ ...form, truckLoads: e.target.value })}
                          placeholder="例：5 柜 / 10 车"
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                        />
                        <select
                          value={form.containerType}
                          onChange={(e) => setForm({ ...form, containerType: e.target.value })}
                          className="w-32 border border-gray-200 rounded-lg px-2 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-red-400"
                        >
                          {["20GP", "40GP", "40HQ", "整车", "半挂车"].map((t) => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Price & deadline */}
                <div>
                  <h4 className="text-gray-700 font-semibold text-sm mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500 rounded-full text-white text-xs font-bold">2</span>
                    价格与交期
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">目标价格区间（/单位）</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={form.minPrice}
                          onChange={(e) => setForm({ ...form, minPrice: e.target.value })}
                          placeholder="最低价"
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                        />
                        <span className="text-gray-400 text-sm flex-shrink-0">—</span>
                        <input
                          type="text"
                          value={form.maxPrice}
                          onChange={(e) => setForm({ ...form, maxPrice: e.target.value })}
                          placeholder="最高价"
                          className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">报价截止日期 <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={form.deadline}
                        onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                      />
                    </div>
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">装运港口</label>
                      <select
                        value={form.shippingPort}
                        onChange={(e) => setForm({ ...form, shippingPort: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-red-400"
                      >
                        <option value="">请选择港口</option>
                        {shippingPorts.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">付款方式</label>
                      <select
                        value={form.paymentTerms}
                        onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer focus:border-red-400"
                      >
                        <option value="">请选择付款方式</option>
                        {paymentTermOptions.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="text-gray-700 font-semibold text-sm mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500 rounded-full text-white text-xs font-bold">3</span>
                    详细需求描述
                  </h4>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="请详细描述您的采购需求，包括产品用途、质量要求、包装要求、交货期等..."
                    rows={4}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 resize-none"
                  />
                  <p className="text-gray-300 text-xs text-right mt-1">{form.description.length}/500</p>
                </div>

                {/* Specs */}
                <div>
                  <h4 className="text-gray-700 font-semibold text-sm mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center bg-red-500 rounded-full text-white text-xs font-bold">4</span>
                    产品规格要求
                  </h4>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={specInput}
                      onChange={(e) => setSpecInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddSpec()}
                      placeholder="例：材质：304不锈钢（按 Enter 添加）"
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                    />
                    <button
                      onClick={handleAddSpec}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-1"></i>添加
                    </button>
                  </div>
                  {specs.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {specs.map((spec, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5 text-sm text-red-600">
                          <span>{spec}</span>
                          <button onClick={() => handleRemoveSpec(idx)} className="text-red-400 hover:text-red-600 cursor-pointer">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Privacy reminder */}
                <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-lock-line text-orange-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-orange-700 font-semibold text-sm">隐私保护提醒</p>
                      <p className="text-orange-600 text-xs mt-1 leading-relaxed">
                        您的采购需求（包括价格、数量、规格、联系方式）将对公众完全隐藏。
                        只有通过平台认证并已缴纳会员费的工厂会员，才能在其专属后台查看您的报价需求。
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => handlePublish(true)}
                    className="flex-1 py-3 rounded-xl font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line mr-1"></i>保存草稿
                  </button>
                  <button
                    onClick={() => handlePublish(false)}
                    className="flex-1 py-3 rounded-xl font-bold text-sm bg-red-500 hover:bg-red-600 text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-1"></i>立即发布
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
